import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, ArrowRight, ArrowLeft, Sparkles, Target, Shield, FileText, Settings, Play,
  LayoutDashboard, Bot, Building2, Key, Globe, MessageSquare, Layers, Search
} from 'lucide-react';

const WelcomeGuide = ({ onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [spotlightStyle, setSpotlightStyle] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
  const [elementFound, setElementFound] = useState(true);
  const tooltipRef = useRef(null);
  const retryCountRef = useRef(0);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const steps = [
    {
      id: 'welcome',
      title: 'Bienvenue sur SYLink Pentest !',
      description: 'Découvrez votre plateforme de pentest autonome pilotée par IA. Ce guide vous présente les fonctionnalités essentielles pour démarrer rapidement.',
      icon: <Sparkles className="w-8 h-8" />,
      selector: null,
      position: 'center'
    },
    {
      id: 'dashboard',
      title: 'Tableau de bord',
      description: 'Votre centre de contrôle ! Visualisez vos statistiques, l\'état de vos scans en cours, et les vulnérabilités détectées en temps réel.',
      icon: <LayoutDashboard className="w-8 h-8" />,
      selector: '[data-tour="dashboard-stats"]',
      fallbackSelector: '.dashboard-stats, .liquid-glass',
      position: 'right'
    },
    {
      id: 'workspaces',
      title: 'Espaces clients - Multi-clients',
      description: 'Organisez vos missions par client ! Chaque workspace isole les scans, rapports et identifiants. Idéal pour les MSP et les auditeurs multi-clients.',
      icon: <Building2 className="w-8 h-8" />,
      selector: 'a[href="/workspaces"]',
      fallbackSelector: 'nav a[href="/workspaces"], [data-tour="workspaces"]',
      position: 'bottom'
    },
    {
      id: 'new-scan',
      title: 'Lancer un scan',
      description: 'Démarrez un nouveau test d\'intrusion en quelques clics. Choisissez entre scan externe, interne (via VPN), ou scan Active Directory.',
      icon: <Target className="w-8 h-8" />,
      selector: '[data-tour="new-scan"]',
      fallbackSelector: 'a[href="/scans/new"], button:contains("Nouveau")',
      position: 'bottom'
    },
    {
      id: 'ai-assistant',
      title: 'Assistant IA',
      description: 'Votre copilote intelligent ! Posez des questions, lancez des scans par commande vocale, ou demandez des analyses. Cliquez sur le bouton violet en bas à droite.',
      icon: <Bot className="w-8 h-8" />,
      selector: '[data-tour="ai-assistant"]',
      fallbackSelector: '.ai-assistant-button, button[title*="Assistant"]',
      position: 'left'
    },
    {
      id: 'credentials',
      title: 'Dark Web & Identifiants',
      description: 'Recherchez les fuites de données sur le Dark Web ! Détectez les identifiants compromis de vos clients via HIBP, DeHashed et plus encore.',
      icon: <Key className="w-8 h-8" />,
      selector: 'a[href="/credentials"]',
      fallbackSelector: 'nav a[href="/credentials"]',
      position: 'bottom'
    },
    {
      id: 'reports',
      title: 'Rapports professionnels',
      description: 'Générez des rapports PDF exécutifs ou techniques avec votre logo. Export PowerPoint, multi-scans, et templates personnalisables inclus.',
      icon: <FileText className="w-8 h-8" />,
      selector: 'a[href="/reports"]',
      fallbackSelector: 'nav a[href="/reports"]',
      position: 'bottom'
    },
    {
      id: 'settings',
      title: 'Paramètres & Agent IA',
      description: 'Configurez votre compte, VPN interne, et l\'Agent IA Supervisor qui surveille automatiquement votre infrastructure et corrige les problèmes.',
      icon: <Settings className="w-8 h-8" />,
      selector: 'a[href="/settings"]',
      fallbackSelector: 'nav a[href="/settings"]',
      position: 'bottom'
    },
    {
      id: 'complete',
      title: 'Vous êtes prêt !',
      description: 'Lancez votre premier scan et laissez l\'IA analyser votre cible. Besoin d\'aide ? L\'Assistant IA est toujours disponible en bas à droite !',
      icon: <Sparkles className="w-8 h-8" />,
      selector: null,
      position: 'center'
    }
  ];

  const currentStepData = steps[currentStep];

  const updatePositions = useCallback(() => {
    const stepData = steps[currentStep];

    if (!stepData.selector) {
      setSpotlightStyle(null);
      setElementFound(true);
      setTooltipPosition({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        position: 'fixed'
      });
      retryCountRef.current = 0;
      return;
    }

    let element = document.querySelector(stepData.selector);
    if (!element && stepData.fallbackSelector) {
      element = document.querySelector(stepData.fallbackSelector);
    }

    if (!element) {
      setElementFound(false);
      if (retryCountRef.current < 10) {
        retryCountRef.current++;
        setTimeout(updatePositions, 200);
        return;
      }
      setSpotlightStyle(null);
      setTooltipPosition({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        position: 'fixed'
      });
      return;
    }

    setElementFound(true);
    retryCountRef.current = 0;

    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

    setTimeout(() => {
      const rect = element.getBoundingClientRect();
      const padding = 8;

      setSpotlightStyle({
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2
      });

      const tooltipWidth = 400;
      const tooltipHeight = 280;
      const margin = 20;
      let newPosition = {};

      switch (stepData.position) {
        case 'bottom':
          newPosition = {
            top: `${rect.bottom + margin}px`,
            left: `${Math.max(margin, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - margin))}px`,
            transform: 'none',
            position: 'fixed'
          };
          break;
        case 'top':
          newPosition = {
            top: `${rect.top - tooltipHeight - margin}px`,
            left: `${Math.max(margin, Math.min(rect.left + rect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - margin))}px`,
            transform: 'none',
            position: 'fixed'
          };
          break;
        case 'left':
          newPosition = {
            top: `${Math.max(margin, rect.top + rect.height / 2 - tooltipHeight / 2)}px`,
            left: `${rect.left - tooltipWidth - margin}px`,
            transform: 'none',
            position: 'fixed'
          };
          break;
        case 'right':
        default:
          newPosition = {
            top: `${Math.max(margin, Math.min(rect.top, window.innerHeight - tooltipHeight - margin))}px`,
            left: `${Math.min(rect.right + margin, window.innerWidth - tooltipWidth - margin)}px`,
            transform: 'none',
            position: 'fixed'
          };
          break;
      }

      const topValue = parseInt(newPosition.top);
      const leftValue = parseInt(newPosition.left);

      if (topValue < margin) newPosition.top = `${margin}px`;
      if (topValue > window.innerHeight - tooltipHeight - margin) {
        newPosition.top = `${window.innerHeight - tooltipHeight - margin}px`;
      }
      if (leftValue < margin) newPosition.left = `${margin}px`;
      if (leftValue > window.innerWidth - tooltipWidth - margin) {
        newPosition.left = `${window.innerWidth - tooltipWidth - margin}px`;
      }

      setTooltipPosition(newPosition);
    }, 300);
  }, [currentStep, steps]);

  useEffect(() => {
    retryCountRef.current = 0;
    updatePositions();

    window.addEventListener('resize', updatePositions);
    return () => window.removeEventListener('resize', updatePositions);
  }, [currentStep, updatePositions]);

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.removeItem('showWelcomeGuide');
    localStorage.setItem('welcomeGuideCompleted', 'true');
    setIsVisible(false);
    setTimeout(() => {
      onComplete?.();
      onClose?.();
    }, 300);
  };

  const handleSkip = () => {
    localStorage.setItem('welcomeGuideSkipped', 'true');
    localStorage.removeItem('showWelcomeGuide');
    setIsVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const isFirstOrLast = currentStep === 0 || currentStep === steps.length - 1;
  const isCenteredStep = !currentStepData.selector;
  const shouldShowOverlay = isCenteredStep || (elementFound && spotlightStyle);

  // Reduced opacity for better visibility (0.5 instead of 0.85)
  const overlayOpacity = 0.5;

  return (
    <>
      {/* Semi-transparent overlay - reduced opacity for visibility */}
      <div
        className={`fixed inset-0 z-[9998] transition-opacity duration-300 pointer-events-auto ${
          isVisible && shouldShowOverlay ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleSkip}
        style={{ background: shouldShowOverlay ? `rgba(0, 0, 0, ${overlayOpacity})` : 'transparent' }}
      >
        {/* SVG mask for spotlight effect */}
        {spotlightStyle && (
          <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
            <defs>
              <mask id="spotlight-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                <rect
                  x={spotlightStyle.left}
                  y={spotlightStyle.top}
                  width={spotlightStyle.width}
                  height={spotlightStyle.height}
                  rx="12"
                  fill="black"
                />
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill={`rgba(0, 0, 0, ${overlayOpacity})`}
              mask="url(#spotlight-mask)"
            />
          </svg>
        )}
      </div>

      {/* Spotlight highlight border */}
      {spotlightStyle && (
        <div
          className="fixed z-[9998] pointer-events-none rounded-xl"
          style={{
            top: spotlightStyle.top,
            left: spotlightStyle.left,
            width: spotlightStyle.width,
            height: spotlightStyle.height,
            boxShadow: '0 0 0 3px #00ff88, 0 0 20px rgba(0, 255, 136, 0.5), 0 0 40px rgba(0, 255, 136, 0.3)',
            animation: 'pulse-spotlight 2s infinite'
          }}
        />
      )}

      {/* Guide Card / Tooltip */}
      <div
        ref={tooltipRef}
        className={`fixed z-[9999] transition-all duration-300 pointer-events-auto ${
          isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
        style={{
          ...tooltipPosition,
          width: '400px',
          maxWidth: 'calc(100vw - 40px)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0d0d0d]/95 backdrop-blur-xl border border-brand-green/40 rounded-2xl shadow-2xl shadow-brand-green/20 overflow-hidden">
          {/* Header */}
          <div className="p-5 bg-gradient-to-r from-brand-green/20 to-cyan-500/10 border-b border-gray-800/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                {steps.map((_, index) => (
                  <div
                    key={index}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      index === currentStep
                        ? 'w-6 bg-brand-green'
                        : index < currentStep
                        ? 'w-1.5 bg-brand-green/60'
                        : 'w-1.5 bg-gray-600'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={handleSkip}
                className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-4 h-4 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center text-brand-green shrink-0">
                {currentStepData.icon}
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-white truncate">
                  {currentStepData.title}
                </h3>
                <p className="text-xs text-brand-green/80">
                  Étape {currentStep + 1} / {steps.length}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            <p className="text-gray-300 text-sm leading-relaxed">
              {currentStepData.description}
            </p>
          </div>

          {/* Actions */}
          <div className="px-5 pb-5 flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              Passer le guide
            </button>

            <div className="flex items-center gap-2">
              {currentStep > 0 && (
                <button
                  onClick={prevStep}
                  className="flex items-center gap-1 px-3 py-2 text-gray-400 hover:text-white transition-colors text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
              )}

              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-4 py-2 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-all text-sm shadow-lg shadow-brand-green/25"
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    Commencer
                    <Sparkles className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Suivant
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Arrow pointer for non-centered positions */}
        {!isFirstOrLast && spotlightStyle && (
          <div
            className="absolute w-4 h-4 bg-[#0d0d0d]/95 transform rotate-45"
            style={{
              ...(currentStepData.position === 'bottom' && {
                top: '-8px',
                left: '50%',
                marginLeft: '-8px',
                borderTop: '1px solid',
                borderLeft: '1px solid',
                borderColor: 'rgba(0, 255, 136, 0.4)'
              }),
              ...(currentStepData.position === 'top' && {
                bottom: '-8px',
                left: '50%',
                marginLeft: '-8px',
                borderBottom: '1px solid',
                borderRight: '1px solid',
                borderColor: 'rgba(0, 255, 136, 0.4)'
              }),
              ...(currentStepData.position === 'left' && {
                right: '-8px',
                top: '50%',
                marginTop: '-8px',
                borderTop: '1px solid',
                borderRight: '1px solid',
                borderColor: 'rgba(0, 255, 136, 0.4)'
              }),
              ...(currentStepData.position === 'right' && {
                left: '-8px',
                top: '30px',
                borderBottom: '1px solid',
                borderLeft: '1px solid',
                borderColor: 'rgba(0, 255, 136, 0.4)'
              })
            }}
          />
        )}
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes pulse-spotlight {
          0%, 100% {
            box-shadow: 0 0 0 3px #00ff88, 0 0 20px rgba(0, 255, 136, 0.5), 0 0 40px rgba(0, 255, 136, 0.3);
          }
          50% {
            box-shadow: 0 0 0 5px #00ff88, 0 0 30px rgba(0, 255, 136, 0.6), 0 0 50px rgba(0, 255, 136, 0.4);
          }
        }
      `}</style>
    </>
  );
};

export default WelcomeGuide;
